/*
 * /tides — приливы: где, когда и что из этого следует.
 *
 * ЧТО ИЗМЕНИЛОСЬ И ПОЧЕМУ (2026-09-02).
 *
 * Инструмент считал приливные задачи по данным, которые пользователь приносил
 * из альманаха, и честно писал, что базы станций у него нет. Замер спроса
 * показал, что это отвечало примерно НИ НА ОДИН реальный запрос: «приливы и
 * отливы» — 53 261 показ в месяц, и внутри почти сплошь места —
 * «в архангельске» 4 226, «на сахалине» 3 534, «в северодвинске» 3 115,
 * «в кандалакшском заливе» 4 848, Камчатка 5 516, «магадан» 1 810,
 * «в мурманске» 1 464. Человек приходит узнать, КОГДА вода, а мы просили
 * принести ответ с собой.
 *
 * Теперь прилив предсказывается здесь: гармонические постоянные лежат рядом
 * файлом, а счёт идёт в браузере. Отсюда свойство, которого нет ни у одного
 * конкурента в нише (tide-forecast, tides4fishing, tideschart, EasyTide — все
 * серверные): БЕЗ СЕТИ. Прилив нужен ровно там, где связи нет.
 *
 * Альманашные режимы никуда не делись — они стали вкладкой «Расчёты» и
 * получили то, чего им не хватало: данные. Ни один приливный сайт не доводит
 * дело до запаса под килем, и ни один навигационный калькулятор не знает
 * прилива. Здесь одно кормит другое, и в этом весь смысл.
 */

import * as E from './engine.js';
import * as P from './predict.js';
import * as ST from './stations.js';
import { renderCalc, setLiveWaters } from './calc.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__TD || { T: {}, cfg: {} };
const T = B.T;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const HOUR = 3600000, DAY = 86400000;

const FREE_DAYS = 3, PRO_DAYS = 14;

const S = {
    mode: 'now',
    place: null,          // {name, lat, lon, tz}
    station: null,        // {lat, lon, km, comps}
    datums: null,         // {lat, hat, range}
    dayOffset: 0,
    unit: 'm',
    /* Поля «запаса и прохода» живут В СОСТОЯНИИ, а не в DOM.
       render() очищает сцену перед перерисовкой, поэтому чтение значения из
       элемента давало ДЕФОЛТ на каждом нажатии клавиши: введённая осадка
       стиралась в тот же миг. Поймал эталонный стенд — на глаз это выглядит
       как «поле не принимает ввод». */
    clr: { chart: 2.4, draft: 1.6, safety: 0.6, bridge: 18.0, hat: null, mast: 19.0 },
    // альманашная часть
    calc: 'height',
    a: { timeH: 6, height: 5.4 },
    b: { timeH: 12.2, height: 0.8 },
    harm: { z0: 3.0, comps: [{ name: 'M2', amp: 2.0, phase: 0 }, { name: 'S2', amp: 0.7, phase: 30 }] },
};

const isPro = () => !!(window.__isProNow && window.__isProNow(false));
const horizonDays = () => (isPro() ? PRO_DAYS : FREE_DAYS);

/* ── формат ──────────────────────────────────────────────────────────────── */
const tz = () => S.place?.tz || 'UTC';
function fmtTime(ms) {
    try {
        return new Intl.DateTimeFormat(B.cfg.locale || 'ru', {
            hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz(),
        }).format(new Date(ms));
    } catch { return new Date(ms).toISOString().slice(11, 16); }
}
function fmtDay(ms, opts) {
    try {
        return new Intl.DateTimeFormat(B.cfg.locale || 'ru',
            Object.assign({ weekday: 'short', day: 'numeric', month: 'short', timeZone: tz() }, opts || {}))
            .format(new Date(ms));
    } catch { return new Date(ms).toISOString().slice(0, 10); }
}
const M_PER_FT = 0.3048;
const toUnit = m => (S.unit === 'ft' ? m / M_PER_FT : m);
const unitLabel = () => (S.unit === 'ft' ? t('unit_ft') : t('unit_m'));
const fmtH = m => toUnit(m).toFixed(S.unit === 'ft' ? 1 : 2);

/** Полночь местного дня `offset` в миллисекундах epoch. */
function localMidnight(offset) {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz(),
    }).formatToParts(now);
    const g = k => parts.find(p => p.type === k).value;
    // Полдень UTC заведомо попадает в те же местные сутки в любом поясе,
    // дальше вычитаем местное смещение — так не нужен свой разбор TZ.
    const noonUTC = Date.parse(`${g('year')}-${g('month')}-${g('day')}T12:00:00Z`);
    const off = tzOffsetMs(noonUTC);
    return noonUTC - 12 * HOUR - off + offset * DAY;
}
function tzOffsetMs(ms) {
    try {
        const d = new Date(ms);
        const s = new Intl.DateTimeFormat('en-US', {
            timeZone: tz(), hour12: false, year: 'numeric', month: '2-digit',
            day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).format(d);
        const m = s.match(/(\d+)\/(\d+)\/(\d+),?\s+(\d+):(\d+):(\d+)/);
        if (!m) return 0;
        const asUTC = Date.UTC(+m[3], +m[1] - 1, +m[2], +m[4] % 24, +m[5], +m[6]);
        return asUTC - (ms - ms % 1000);
    } catch { return 0; }
}

/* ── выбор места ─────────────────────────────────────────────────────────── */
let searchTimer = null;
function wirePlaceSearch() {
    const input = $('tdPlace'), list = $('tdPlaceList');
    if (!input) return;
    const close = () => { list.hidden = true; list.textContent = ''; };

    input.addEventListener('input', () => {
        clearTimeout(searchTimer);
        const q = input.value;
        searchTimer = setTimeout(async () => {
            let res;
            try {
                await ST.loadPlaces();
                res = await ST.searchPlaces(q, 8);
            } catch { return; }
            list.textContent = '';
            if (!res.length) { close(); return; }
            for (const p of res) {
                const b = el('button', 'td-opt');
                b.type = 'button';
                b.append(el('span', null, p.name), el('span', 'td-opt-c', p.country));
                b.addEventListener('click', () => { close(); input.value = p.name; setPlace(p); });
                list.append(b);
            }
            list.hidden = false;
        }, 160);
    });
    input.addEventListener('blur', () => setTimeout(close, 180));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
}

async function setPlace(p) {
    S.place = p;
    S.dayOffset = 0;
    try { window.__toolSettings?.save('tides', 'place', p); } catch { /* приватный режим */ }
    await ensureStations();
    const near = ST.ready() ? ST.nearest(p.lat, p.lon) : null;
    S.station = near;
    S.datums = near ? P.datums(near.comps) : null;
    window.__stage?.(near ? 'place_resolved' : 'place_no_station');
    render();
}

let stationsErr = null;
async function ensureStations() {
    if (ST.ready()) return true;
    try { await ST.loadStations(); stationsErr = null; return true; }
    catch (e) { stationsErr = e; return false; }
}

/* ── кривая уровня ───────────────────────────────────────────────────────── */
function drawTide(canvas, fromMs, hours, opts = {}) {
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 640, H = canvas.clientHeight || 220;
    if (canvas.width !== Math.round(W * dpr)) {
        canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    if (!S.station) return;

    const c = P.curve(S.station.comps, fromMs, hours, 10);
    let lo = Infinity, hi = -Infinity;
    for (const v of c.h) { if (v < lo) lo = v; if (v > hi) hi = v; }
    const pad = Math.max(0.15, (hi - lo) * 0.18);
    lo -= pad; hi += pad;

    const cs = getComputedStyle(document.documentElement);
    const ink = (cs.getPropertyValue('--text-muted') || '#8b97a8').trim();
    const grid = 'rgba(140,160,190,.16)';
    const accent = '#4aa3ff';

    const padL = 40, padR = 10, padT = 10, padB = 22;
    const X = ms => padL + ((ms - fromMs) / (hours * HOUR)) * (W - padL - padR);
    const Y = h => padT + (1 - (h - lo) / Math.max(1e-9, hi - lo)) * (H - padT - padB);

    ctx.font = '11px ui-monospace, Menlo, monospace';
    ctx.strokeStyle = grid; ctx.lineWidth = 1; ctx.fillStyle = ink;
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    const span = hi - lo;
    const stepH = span > 8 ? 2 : span > 4 ? 1 : span > 1.6 ? 0.5 : 0.2;
    for (let h = Math.ceil(lo / stepH) * stepH; h <= hi; h += stepH) {
        ctx.beginPath(); ctx.moveTo(padL, Y(h)); ctx.lineTo(W - padR, Y(h)); ctx.stroke();
        ctx.fillText(fmtH(h), padL - 5, Y(h));
    }
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    const hourStep = hours <= 30 ? 3 : hours <= 80 ? 12 : 24;
    for (let k = 0; k <= hours; k += hourStep) {
        const ms = fromMs + k * HOUR;
        ctx.beginPath(); ctx.moveTo(X(ms), padT); ctx.lineTo(X(ms), H - padB); ctx.stroke();
        ctx.fillText(hours > 30 ? fmtDay(ms, { weekday: undefined, month: undefined, day: 'numeric' }) : fmtTime(ms), X(ms), H - padB + 4);
    }

    // Нуль глубин: линия, ниже которой на карте глубин ничего не обещано.
    if (S.datums && S.datums.lat > lo && S.datums.lat < hi) {
        ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(245,158,11,.7)';
        ctx.beginPath(); ctx.moveTo(padL, Y(S.datums.lat)); ctx.lineTo(W - padR, Y(S.datums.lat)); ctx.stroke();
        ctx.setLineDash([]);
    }

    // Заливка под кривой — вода читается как вода, а не как график продаж.
    ctx.beginPath();
    for (let i = 0; i < c.h.length; i++) {
        const x = X(c.t[i]), y = Y(c.h[i]);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.lineTo(X(c.t[c.t.length - 1]), H - padB);
    ctx.lineTo(X(c.t[0]), H - padB);
    ctx.closePath();
    const g = ctx.createLinearGradient(0, padT, 0, H - padB);
    g.addColorStop(0, 'rgba(74,163,255,.30)');
    g.addColorStop(1, 'rgba(74,163,255,.04)');
    ctx.fillStyle = g; ctx.fill();

    ctx.beginPath();
    for (let i = 0; i < c.h.length; i++) {
        const x = X(c.t[i]), y = Y(c.h[i]);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.strokeStyle = accent; ctx.lineWidth = 2.2; ctx.stroke();

    // Полные и малые воды подписаны прямо на кривой — в этом весь ответ.
    if (opts.labelExtremes !== false) {
        const ex = P.extremes(c);
        ctx.font = '600 11px system-ui, sans-serif';
        for (const e of ex) {
            const x = X(e.ms), y = Y(e.h);
            ctx.beginPath(); ctx.arc(x, y, 3.5, 0, 7);
            ctx.fillStyle = e.type === 'high' ? '#22c55e' : '#f59e0b'; ctx.fill();
            if (hours <= 30) {
                ctx.fillStyle = ink; ctx.textAlign = 'center';
                ctx.textBaseline = e.type === 'high' ? 'bottom' : 'top';
                ctx.fillText(fmtTime(e.ms), x, y + (e.type === 'high' ? -7 : 7));
            }
        }
    }

    // «Сейчас».
    const now = Date.now();
    if (now >= fromMs && now <= fromMs + hours * HOUR) {
        ctx.strokeStyle = '#ef4444'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(X(now), padT); ctx.lineTo(X(now), H - padB); ctx.stroke();
        ctx.setLineDash([]);
        const hNow = P.heightAt(S.station.comps, now);
        ctx.beginPath(); ctx.arc(X(now), Y(hNow), 4.5, 0, 7);
        ctx.fillStyle = '#ef4444'; ctx.fill();
    }
}

/* ── карточки метрик ─────────────────────────────────────────────────────── */
function stat(label, value, sub, tone) {
    const d = el('div', 'tc-stat' + (tone ? ' ' + tone : ''));
    d.append(el('div', 'tc-stat-l', label), el('div', 'tc-stat-v', value));
    if (sub) d.append(el('div', 'tc-stat-s', sub));
    return d;
}

function durationText(ms) {
    const mins = Math.max(0, Math.round(ms / 60000));
    const h = Math.floor(mins / 60), m = mins % 60;
    return h ? t('dur_hm', { h, m: String(m).padStart(2, '0') }) : t('dur_m', { m });
}

/* ── режим «сейчас» ──────────────────────────────────────────────────────── */
function renderNow(host) {
    const now = Date.now();
    const from = now - 6 * HOUR;
    const wrap = el('div', 'tc-panel tc-full');
    const cv = el('canvas', 'tc-canvas');
    cv.height = 220; cv.style.height = '220px';
    wrap.append(cv);
    host.append(wrap);

    const h = P.heightAt(S.station.comps, now);
    const ev = P.nextEvents(S.station.comps, now, 4);
    const stats = el('div', 'tc-stats');

    const rising = ev[0]?.type === 'high';
    stats.append(stat(t('f_now'), `${fmtH(h)} ${unitLabel()}`,
        rising ? t('f_now_rising') : t('f_now_falling'), 'lead'));

    if (ev[0]) {
        stats.append(stat(ev[0].type === 'high' ? t('f_next_hw') : t('f_next_lw'),
            fmtTime(ev[0].ms),
            t('f_in', { d: durationText(ev[0].ms - now), h: fmtH(ev[0].h) + ' ' + unitLabel() })));
    }
    if (ev[1]) {
        stats.append(stat(ev[1].type === 'high' ? t('f_next_hw') : t('f_next_lw'),
            fmtTime(ev[1].ms),
            t('f_in', { d: durationText(ev[1].ms - now), h: fmtH(ev[1].h) + ' ' + unitLabel() })));
    }

    const dr = P.dayRange(S.station.comps, localMidnight(0), S.datums);
    const springish = dr.pct > 0.66 ? t('spring') : dr.pct < 0.36 ? t('neap') : t('mid');
    stats.append(stat(t('f_range'), `${fmtH(dr.range)} ${unitLabel()}`, springish));

    const statPanel = el('div', 'tc-panel tc-full');
    statPanel.append(stats);
    host.append(statPanel);
    drawTideDeferred(cv, from, 30);
}

/* ── режим «дни» ─────────────────────────────────────────────────────────── */
function renderDays(host) {
    const days = horizonDays();
    const panel = el('div', 'tc-panel tc-full tc-flush');
    const wrap = el('div', 'tc-tablewrap');
    const tb = el('table', 'tc-table');
    const thead = el('thead');
    const hr = el('tr');
    for (const k of ['th_day', 'th_hw1', 'th_lw1', 'th_hw2', 'th_lw2', 'th_range']) hr.append(el('th', null, t(k)));
    thead.append(hr); tb.append(thead);
    const body = el('tbody');

    const start = localMidnight(0);
    const c = P.curve(S.station.comps, start - 2 * HOUR, days * 24 + 4, 10);
    const ex = P.extremes(c);
    const todayKey = fmtDay(Date.now());

    for (let d = 0; d < days; d++) {
        const d0 = start + d * DAY, d1 = d0 + DAY;
        const inDay = ex.filter(e => e.ms >= d0 && e.ms < d1);
        const highs = inDay.filter(e => e.type === 'high');
        const lows = inDay.filter(e => e.type === 'low');
        const r = el('tr');
        if (fmtDay(d0) === todayKey) r.className = 'now';
        r.append(el('td', null, fmtDay(d0)));
        const cell = e => {
            const td = el('td', 'num');
            td.textContent = e ? `${fmtTime(e.ms)} · ${fmtH(e.h)}` : '—';
            return td;
        };
        r.append(cell(highs[0]), cell(lows[0]), cell(highs[1]), cell(lows[1]));
        let lo = Infinity, hi = -Infinity;
        for (const e of inDay) { if (e.h < lo) lo = e.h; if (e.h > hi) hi = e.h; }
        r.append(el('td', 'num', inDay.length ? fmtH(hi - lo) : '—'));
        body.append(r);
    }
    tb.append(body); wrap.append(tb); panel.append(wrap);
    host.append(panel);

    if (!isPro()) {
        const p = el('div', 'tc-msg');
        p.append(document.createTextNode(t('days_shown', { n: FREE_DAYS, pro: PRO_DAYS }) + ' '));
        const a = el('a', 'tc-perk', t('perk_more_days'));
        a.href = `/${B.cfg.locale || 'ru'}/pro`;
        p.append(a);
        host.append(p);
    }

    const acts = el('div', 'tc-actions');
    const ics = el('button', 'tc-btn ghost', t('btn_ics'));
    ics.type = 'button';
    ics.addEventListener('click', () => exportIcs(ex));
    const csv = el('button', 'tc-btn ghost', t('btn_csv'));
    csv.type = 'button';
    csv.addEventListener('click', () => exportCsv(ex, days, start));
    acts.append(ics, csv);
    host.append(acts);
}

function icsEscape(s) { return String(s).replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n'); }
function icsStamp(ms) { return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''); }

function exportIcs(ex) {
    if (!isPro()) { upsell('ics'); return; }
    const name = S.place?.name || 'tide';
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Timbrica//Tides//EN', 'CALSCALE:GREGORIAN'];
    for (const e of ex) {
        lines.push('BEGIN:VEVENT',
            `UID:${e.ms}-${e.type}@timbrica`,
            `DTSTAMP:${icsStamp(Date.now())}`,
            `DTSTART:${icsStamp(e.ms)}`,
            `DURATION:PT10M`,
            `SUMMARY:${icsEscape((e.type === 'high' ? t('hw') : t('lw')) + ' ' + name + ' · ' + fmtH(e.h) + ' ' + unitLabel())}`,
            'END:VEVENT');
    }
    lines.push('END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
    window.__downloadFile?.(blob, `${slugify(name)}-tides.ics`);
}

function exportCsv(ex, days, start) {
    const rows = [['datetime_local', 'type', 'height_' + S.unit].join(',')];
    for (const e of ex) {
        if (e.ms < start || e.ms > start + days * DAY) continue;
        rows.push([`${fmtDay(e.ms, { weekday: undefined, year: 'numeric', month: '2-digit', day: '2-digit' })} ${fmtTime(e.ms)}`,
            e.type, fmtH(e.h)].join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    window.__downloadFile?.(blob, `${slugify(S.place?.name || 'tide')}-tides.csv`);
}

const slugify = s => String(s).toLowerCase().replace(/[^\wЀ-ӿ]+/g, '-').replace(/^-|-$/g, '') || 'tide';

function upsell(what) {
    window.__toast?.(t('perk_locked'), 'info');
    window.__aeFunnel?.('perk_upsell', { what });
}

/* ── режим «запас под килем» — прилив уже известен ───────────────────────── */
function renderClearance(host) {
    const now = Date.now();
    const panel = el('div', 'tc-panel tc-full');
    const g = el('div', 'tc-fields');
    // HAT над нулём глубин известен из модели — подставляем, пока не тронули.
    if (S.clr.hat === null) {
        S.clr.hat = S.datums ? +(S.datums.hat - S.datums.lat).toFixed(1) : 5.2;
    }
    const mk = (id, label, key, step) => {
        const f = el('div', 'tc-f');
        const l = el('label', 'tc-f-l', label); l.setAttribute('for', id);
        const i = el('input', 'tc-in'); i.type = 'number'; i.id = id; i.value = S.clr[key]; i.step = step || '0.1';
        i.addEventListener('input', () => {
            const v = Number(i.value);
            if (Number.isFinite(v)) { S.clr[key] = v; render(); }
        });
        f.append(l, i); return f;
    };
    g.append(
        mk('tdChart', t('lbl_charted'), 'chart'),
        mk('tdDraft', t('lbl_draft'), 'draft'),
        mk('tdSafety', t('lbl_safety'), 'safety'),
        mk('tdBridge', t('lbl_bridge'), 'bridge'),
        mk('tdHat', t('lbl_hat'), 'hat'),
        mk('tdMast', t('lbl_mast'), 'mast'),
    );
    panel.append(g);

    const h = P.heightAt(S.station.comps, now);
    // Глубина с карты отсчитывается от нуля глубин, а модель считает от
    // среднего уровня — без этого перевода число было бы завышено на |LAT|.
    const tideAboveCD = S.datums ? h - S.datums.lat : h;
    const uk = E.underKeel(S.clr.chart, tideAboveCD, S.clr.draft, S.clr.safety);

    const stats = el('div', 'tc-stats');
    stats.id = 'tdClrOut';
    stats.append(stat(t('f_tide_cd'), `${fmtH(tideAboveCD)} ${unitLabel()}`, t('f_tide_cd_sub')));
    stats.append(stat(t('f_water'), `${fmtH(uk.water)} ${unitLabel()}`, t('f_water_sub')));
    stats.append(stat(t('f_ukc'), `${fmtH(uk.clearance)} ${unitLabel()}`,
        uk.ok ? t('f_ukc_ok') : t('f_ukc_bad'), uk.ok ? 'good' : 'bad'));
    if (!uk.ok) {
        stats.append(stat(t('f_need_tide'), `${fmtH(uk.needTide)} ${unitLabel()}`,
            t('f_need_tide_sub'), 'warn'));
    }
    panel.append(stats);

    /*
     * Просвет под мостом.
     *
     * Считается от НАИВЫСШЕГО уровня (HAT), а не от нуля глубин, поэтому при
     * малой воде просвет БОЛЬШЕ — знак противоположен привычке по глубинам, и
     * ошибаются здесь чаще всего. Блок был в инструменте до переделки и едва
     * не исчез вместе с ней: ключи перевода остались, а рисовать их стало
     * нечему. Теперь HAT ещё и подставляется из модели (над нулём глубин),
     * то есть исчезла главная причина ошибиться — искать его вручную.
     */
    const br = E.bridgeClearance(S.clr.bridge, S.clr.hat, tideAboveCD, S.clr.mast, S.clr.safety);
    const bstats = el('div', 'tc-stats');
    bstats.id = 'tdBridgeOut';
    bstats.append(stat(t('f_bridge'), `${fmtH(br.available)} ${unitLabel()}`, t('f_bridge_sub')));
    bstats.append(stat(t('f_mast_clr'), `${fmtH(br.clearance)} ${unitLabel()}`,
        br.ok ? t('f_ukc_ok') : t('f_ukc_bad'), br.ok ? 'good' : 'bad'));
    bstats.append(stat(t('f_max_tide'), `${fmtH(br.maxTide)} ${unitLabel()}`, t('f_max_tide_sub')));
    panel.append(el('div', 'tc-eyebrow', t('bridge_title')), bstats);
    panel.append(el('p', 'tc-note', t('bridge_note')));

    // Окно прохода на сутки: когда запас держится.
    const need = S.clr.draft + S.clr.safety - S.clr.chart;
    const start = localMidnight(S.dayOffset);
    const c = P.curve(S.station.comps, start, 24, 10);
    const gates = [];
    let open = null;
    for (let i = 0; i < c.h.length; i++) {
        const above = (c.h[i] - (S.datums ? S.datums.lat : 0)) >= need;
        if (above && open === null) open = c.t[i];
        if (!above && open !== null) { gates.push([open, c.t[i]]); open = null; }
    }
    if (open !== null) gates.push([open, c.t[c.t.length - 1]]);

    const msg = el('div', 'tc-msg' + (gates.length ? ' good' : ' bad'));
    /* ⚠️ Сравнивать `need` с S.datums.lat нельзя: `need` — это требуемая
       высота НАД НУЛЁМ ГЛУБИН, а lat задан от среднего уровня. Первая
       редакция смешала две системы отсчёта, и окно прохода печаталось как
       «00:00–00:00» вместо «хватает круглые сутки»: условие «всегда» не
       срабатывало, а весь день попадал в один интервал, чьи концы совпадали
       по формату времени. Над нулём глубин «всегда» — это ровно need ≤ 0. */
    if (need <= 0) msg.textContent = t('gate_always');
    else if (!gates.length) msg.textContent = t('gate_never');
    else {
        msg.textContent = t('gate_window', {
            list: gates.map(([a, b]) => `${fmtTime(a)}–${fmtTime(b)}`).join(', '),
            need: fmtH(need) + ' ' + unitLabel(),
        });
    }
    panel.append(msg);
    panel.append(el('p', 'tc-note', t('clearance_note')));
    host.append(panel);
}

/* ── отрисовка после раскладки ───────────────────────────────────────────── */
/*
 * Канвас меряется ПОСЛЕ того, как раскладка устоялась: замер сразу после
 * вставки даёт clientWidth от ещё несхлопнувшейся сетки, и график выходит
 * шире панели. Один кадр ожидания решает это без таймеров-угадаек.
 */
function drawTideDeferred(canvas, from, hours, opts) {
    requestAnimationFrame(() => requestAnimationFrame(() => drawTide(canvas, from, hours, opts)));
}

/* ── общая отрисовка ─────────────────────────────────────────────────────── */
function render() {
    const stage = $('tdStage');
    if (!stage) return;
    stage.textContent = '';

    // Шапка места.
    const head = $('tdPlaceInfo');
    if (head) {
        head.textContent = '';
        if (S.place && S.station) {
            head.append(el('span', null, t('station_from', {
                km: Math.round(S.station.km),
                lat: S.station.lat.toFixed(2), lon: S.station.lon.toFixed(2),
            })));
        } else if (S.place && !S.station) {
            head.append(el('span', null, t('station_none')));
        }
    }

    // Расчёты работают ВСЕГДА: это единственный режим, которому место не
    // нужно — он про арифметику над числами из пособия. Прятать его за
    // выбором места значило бы отнять инструмент у того, кто пришёл именно
    // с книгой в руках.
    if (S.mode === 'calc') { publishLiveWaters(); renderCalc(stage); syncTabs(); return; }

    if (!S.place) { stage.append(emptyState()); syncTabs(); return; }
    if (stationsErr) { stage.append(errState()); syncTabs(); return; }
    if (!S.station) { stage.append(noStationState()); syncTabs(); return; }

    if (S.mode === 'now') renderNow(stage);
    else if (S.mode === 'days') renderDays(stage);
    else if (S.mode === 'clearance') renderClearance(stage);
    syncTabs();
}

/**
 * Сегодняшние полная и малая вода выбранного места — в формате, который
 * понимает альманашный калькулятор. Это и есть шов между двумя половинами
 * инструмента: данные слева, расчёт справа.
 */
function publishLiveWaters() {
    if (!S.station || !S.place) { setLiveWaters(null); return; }
    const start = localMidnight(0);
    const ex = P.extremes(P.curve(S.station.comps, start, 30, 10));
    const hw = ex.find(e => e.type === 'high');
    const lw = ex.find(e => e.type === 'low');
    if (!hw || !lw) { setLiveWaters(null); return; }
    const toH = ms => {
        const off = tzOffsetMs(ms);
        return ((ms + off) - Math.floor((ms + off) / DAY) * DAY) / HOUR;
    };
    const first = hw.ms < lw.ms ? hw : lw, second = hw.ms < lw.ms ? lw : hw;
    let ah = toH(first.ms), bh = toH(second.ms);
    if (bh <= ah) bh += 24;
    // Модель считает от среднего уровня; в пособии высоты даны от нуля
    // глубин, поэтому переносим в ту же систему, иначе запас поедет на |LAT|.
    const z = S.datums ? S.datums.lat : 0;
    setLiveWaters({
        place: S.place.name,
        a: { timeH: ah, height: first.h - z },
        b: { timeH: bh, height: second.h - z },
    });
}

function emptyState() {
    const p = el('div', 'tc-panel tc-full');
    p.append(el('p', 'tc-title', t('empty_title')));
    p.append(el('p', 'tc-hint', t('empty_hint')));
    return p;
}
function errState() {
    const p = el('div', 'tc-panel tc-full');
    p.append(el('p', 'tc-title', t('data_err_title')));
    p.append(el('p', 'tc-hint', t('data_err_hint')));
    return p;
}
function noStationState() {
    const p = el('div', 'tc-panel tc-full');
    p.append(el('p', 'tc-title', t('inland_title')));
    p.append(el('p', 'tc-hint', t('inland_hint')));
    return p;
}

function syncTabs() {
    document.querySelectorAll('[data-tdmode]').forEach(b => {
        b.classList.toggle('on', b.dataset.tdmode === S.mode);
        b.setAttribute('aria-selected', b.dataset.tdmode === S.mode ? 'true' : 'false');
    });
}

/* ── старт ───────────────────────────────────────────────────────────────── */
async function boot() {
    wirePlaceSearch();

    document.querySelectorAll('[data-tdmode]').forEach(b => {
        b.addEventListener('click', () => {
            S.mode = b.dataset.tdmode;
            window.__stage?.('mode_' + S.mode);
            render();
        });
    });

    const unitSel = $('tdUnit');
    if (unitSel) {
        window.__toolSettings?.bind('tides', unitSel, 'unit', 'm');
        S.unit = unitSel.value || 'm';
        unitSel.addEventListener('change', () => { S.unit = unitSel.value; render(); });
    }

    // Восстановление последнего места — приливы смотрят про одно и то же
    // место снова и снова, и заставлять искать его каждый раз бессмысленно.
    let saved = null;
    try { saved = window.__toolSettings?.load('tides', 'place', null); } catch { /* noop */ }
    if (saved && Number.isFinite(saved.lat)) {
        const input = $('tdPlace');
        if (input) input.value = saved.name || '';
        await setPlace(saved);
    } else {
        render();
    }

    // Пересчёт «сейчас» раз в минуту: страница часто остаётся открытой.
    setInterval(() => { if (S.mode === 'now' && S.station) render(); }, 60000);

    window.addEventListener('resize', () => {
        clearTimeout(window.__tdRz);
        window.__tdRz = setTimeout(() => { if (S.station) render(); }, 180);
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
